import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    DEFAULT_THEME,
    STORAGE_KEY,
    THEMES,
    getTheme,
    normalizeTheme,
} from '../data/themes';
import { Check, ChevronDown, Palette } from './Icons';

const readStoredTheme = () => {
    if (typeof window === 'undefined') return DEFAULT_THEME;
    return normalizeTheme(window.localStorage.getItem(STORAGE_KEY)) || DEFAULT_THEME;
};

const swatchStyle = theme => ({
    backgroundImage: `linear-gradient(135deg, ${theme.swatch[0]} 0 34%, ${theme.swatch[1]} 34% 67%, ${theme.swatch[2]} 67%)`,
});

/** Header control for switching the whole look of the site. */
export default function ThemePicker() {
    const [theme, setTheme] = useState(readStoredTheme);
    const [open, setOpen] = useState(false);
    const root = useRef(null);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        window.localStorage.setItem(STORAGE_KEY, theme);
        document
            .querySelector('meta[name="theme-color"]')
            ?.setAttribute('content', getTheme(theme)?.swatch[0] || '#07080d');
    }, [theme]);

    // Close on outside click or Escape, and return focus to the trigger.
    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = event => {
            if (root.current && !root.current.contains(event.target)) setOpen(false);
        };
        const onKeyDown = event => {
            if (event.key === 'Escape') {
                setOpen(false);
                root.current?.querySelector('.theme-trigger')?.focus();
            }
        };

        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const choose = useCallback(id => {
        setTheme(id);
        setOpen(false);
    }, []);

    const current = getTheme(theme) || THEMES[0];

    return (
        <div className="theme-picker" ref={root}>
            <button
                type="button"
                className="btn theme-trigger"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={`Theme: ${current.name} ${current.variant}. Change theme`}
                onClick={() => setOpen(value => !value)}
            >
                <Palette className="theme-trigger-glyph" />
                <span className="theme-swatch" style={swatchStyle(current)} aria-hidden="true" />
                <span className="theme-trigger-label">{current.name}</span>
                <ChevronDown className="theme-caret" />
            </button>

            {open && (
                <div className="theme-menu" role="listbox" aria-label="UI theme">
                    <p className="theme-menu-label">Pick a look</p>
                    {THEMES.map(item => (
                        <button
                            key={item.id}
                            type="button"
                            role="option"
                            aria-selected={item.id === theme}
                            className="theme-option"
                            onClick={() => choose(item.id)}
                        >
                            <span className="theme-swatch" style={swatchStyle(item)} aria-hidden="true" />
                            <span className="theme-option-text">
                                <strong>
                                    {item.name} <em>{item.variant}</em>
                                </strong>
                                <span>{item.note}</span>
                            </span>
                            {item.id === theme && <Check className="theme-check" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
